import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Box,
  Select,
  Image,
  Flex,
  useToast,
  Textarea,
  VStack,
  HStack,
  IconButton,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
  Badge,
  Switch,
  Divider,
  SimpleGrid,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon, InfoIcon } from '@chakra-ui/icons';
import { MdCloudUpload, MdAttachFile } from 'react-icons/md';
import { resolveBusinessPanelMediaUrl } from '../../../../utils/cloudinaryDeliveryUrl';

const AddProductModal = ({ isOpen, onClose, onAdd, categories, products = [], editingProduct = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    vatRate: 10, // Default %10 KDV
    categoryId: '',
    image: null,
    description: '',
    ingredients: [],
    extras: [],
    options: [], // Options eklendi
    preparationTimeMin: 15,
    preparationTimeMax: 20,
    isActive: true,
    isFeatured: false,
    isPackage: false,
    packageItems: [],
    tags: [],
    allergens: [],
    nutritionInfo: {
      calories: ''
    }
  });
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [newExtra, setNewExtra] = useState({ name: '', price: '' });
  const [newIngredient, setNewIngredient] = useState({ name: '', removable: true });
  const [selectedPackageProduct, setSelectedPackageProduct] = useState('');
  const [packageQuantity, setPackageQuantity] = useState(1);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [newTag, setNewTag] = useState('');
  const [newAllergen, setNewAllergen] = useState('');
  const [newOption, setNewOption] = useState({ name: '', required: false, choices: [] });
  const [newChoice, setNewChoice] = useState({ name: '', price: 0 });
  const toast = useToast();

  // Edit modunda formData'yı doldur
  useEffect(() => {
    if (editingProduct && isOpen) {
      const product = editingProduct;
      let imageUrl = null;
      
      if (product.image) {
        const apiUrl =
          import.meta.env.REACT_APP_API_URL ||
          process.env.REACT_APP_API_URL ||
          'http://localhost:5001';
        imageUrl = resolveBusinessPanelMediaUrl(product.image, apiUrl, 'wide800');
        setExistingImageUrl(imageUrl);
      }
      
      // Description array ise string'e çevir (her satır bir özellik)
      let descriptionText = '';
      if (product.description) {
        if (Array.isArray(product.description)) {
          descriptionText = product.description.join('\n');
        } else {
          descriptionText = product.description;
        }
      }
      
      const packageItemsData = product.packageItems || [];
      
      // price, extras ve options zaten KDV dahil, direkt göster
      const vatRate = product.vatRate || 10;
      const price = product.price || '';
      
      // Extras zaten KDV dahil, direkt kullan
      const extras = product.extras || [];
      
      // Options zaten KDV dahil, direkt kullan
      const options = product.options || [];
      
      setFormData({
        name: product.name || '',
        price: price,
        vatRate: vatRate,
        categoryId: product.categoryId || '',
        image: null, // Yeni görsel seçilmediyse null
        description: descriptionText,
        ingredients: product.ingredients || [],
        extras: extras,
        options: options,
        preparationTimeMin: product.preparationTimeMin || 15,
        preparationTimeMax: product.preparationTimeMax || 20,
        isActive: product.isActive !== undefined ? product.isActive : true,
        isFeatured: product.isFeatured || false,
        isPackage: product.isPackage || false,
        packageItems: packageItemsData,
        tags: product.tags || [],
        allergens: product.allergens || [],
        nutritionInfo: product.nutritionInfo || { calories: '' }
      });
      
      // Paket ürünü ise originalPrice'ı hesapla
      if (product.isPackage && packageItemsData.length > 0) {
        let total = 0;
        packageItemsData.forEach(item => {
          const p = products.find(pr => pr._id === item.productId);
          if (p) {
            total += p.price * (item.quantity || 1);
          }
        });
        setOriginalPrice(total);
      } else {
        setOriginalPrice(0);
      }
    } else if (!editingProduct && isOpen) {
      // Yeni ürün modunda formu sıfırla
      setFormData({
        name: '',
        price: '',
        vatRate: 10,
        categoryId: '',
        image: null,
        description: '',
        ingredients: [],
        extras: [],
        options: [],
        preparationTimeMin: 15,
        preparationTimeMax: 20,
        isActive: true,
        isFeatured: false,
        isPackage: false,
        packageItems: [],
        tags: [],
        allergens: [],
        nutritionInfo: {
          calories: ''
        }
      });
      setExistingImageUrl(null);
      setOriginalPrice(0);
    }
  }, [editingProduct, isOpen, products]);

  // Manuel eklenebilir tag seçenekleri (new ve popular otomatik olacak)
  const availableTags = ['vegan', 'vejetaryen', 'glutensiz', 'halal', 'acılı'];
  
  // Standart alerjen listesi (EU ve Türkiye standartları)
  const standardAllergens = [
    { value: 'gluten', label: 'Gluten (Buğday, Arpa, Çavdar, Yulaf)' },
    { value: 'süt', label: 'Süt ve Süt Ürünleri' },
    { value: 'yumurta', label: 'Yumurta' },
    { value: 'balık', label: 'Balık' },
    { value: 'kabuklu_deniz_ürünleri', label: 'Kabuklu Deniz Ürünleri (Karides, Istakoz, Yengeç)' },
    { value: 'yer_fıstığı', label: 'Yer Fıstığı' },
    { value: 'soya', label: 'Soya ve Soya Ürünleri' },
    { value: 'ağaç_kuruyemişi', label: 'Ağaç Kuruyemişi (Badem, Ceviz, Fındık, Kaju, vb.)' },
    { value: 'susam', label: 'Susam ve Susam Ürünleri' },
    { value: 'kükürt_dioksit', label: 'Kükürt Dioksit ve Sülfitler' },
    { value: 'yumuşakçalar', label: 'Yumuşakçalar (Midye, İstiridye, vb.)' },
    { value: 'acı_bakla', label: 'Acı Bakla (Lupin)' },
    { value: 'hardal', label: 'Hardal' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePrepTimeChange = (type, value) => {
    setFormData({
      ...formData,
      [type]: value,
    });
  };

  const handleAddIngredient = () => {
    if (newIngredient.name.trim()) {
      setFormData({
        ...formData,
        ingredients: [...formData.ingredients, newIngredient],
      });
      setNewIngredient({ name: '', removable: true });
    }
  };

  const handleRemoveIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index),
    });
  };

  const handleAddExtra = () => {
    if (newExtra.name.trim()) {
      // Fiyat zaten KDV dahil giriliyor, direkt kaydet
      const price = Number(newExtra.price) || 0;
      
      setFormData({
        ...formData,
        extras: [...formData.extras, {
          ...newExtra,
          price: price // KDV dahil fiyat (direkt kaydet)
        }],
      });
      setNewExtra({ name: '', price: '' });
    }
  };

  const handleRemoveExtra = (index) => {
    setFormData({
      ...formData,
      extras: formData.extras.filter((_, i) => i !== index),
    });
  };

  // Options handler'ları
  const handleAddOption = () => {
    if (newOption.name.trim() && newOption.choices.length > 0) {
      // Choices'lar zaten KDV dahil (handleAddChoiceToOption'da hesaplandı)
      setFormData({
        ...formData,
        options: [...formData.options, { ...newOption }],
      });
      setNewOption({ name: '', required: false, choices: [] });
      setNewChoice({ name: '', price: 0 });
    } else {
      toast({
        title: 'Uyarı',
        description: 'Lütfen seçenek adı ve en az bir seçim ekleyin',
        status: 'warning',
        duration: 3000,
      });
    }
  };

  const handleRemoveOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    });
  };

  const handleAddChoiceToOption = () => {
    if (newChoice.name.trim()) {
      // Fiyat zaten KDV dahil giriliyor, direkt kaydet
      const price = Number(newChoice.price) || 0;
      
      setNewOption({
        ...newOption,
        choices: [...newOption.choices, { 
          ...newChoice, 
          price: price // KDV dahil fiyat (direkt kaydet)
        }],
      });
      setNewChoice({ name: '', price: 0 });
    }
  };

  const handleRemoveChoiceFromOption = (choiceIndex) => {
    setNewOption({
      ...newOption,
      choices: newOption.choices.filter((_, i) => i !== choiceIndex),
    });
  };

  // Paket özellikleri
  const handlePackageToggle = (e) => {
    const isPackage = e.target.checked;
    setFormData({
      ...formData,
      isPackage: isPackage,
      packageItems: isPackage ? formData.packageItems : [],
      // Paket ürünlerde de ingredients ve extras olabilir, temizleme
      // extras: isPackage ? [] : formData.extras // Paket ürünlerde extras olmaz
    });
    if (!isPackage) {
      setOriginalPrice(0);
    } else {
      calculateOriginalPrice(formData.packageItems);
    }
  };

  const handleAddPackageItem = () => {
    if (!selectedPackageProduct) {
      toast({
        title: 'Uyarı',
        description: 'Lütfen bir ürün seçin',
        status: 'warning',
        duration: 2000
      });
      return;
    }

    // Aynı ürün zaten eklenmiş mi kontrol et
    const existingItem = formData.packageItems.find(item => item.productId === selectedPackageProduct);
    if (existingItem) {
      toast({
        title: 'Uyarı',
        description: 'Bu ürün zaten pakete eklenmiş',
        status: 'warning',
        duration: 2000
      });
      return;
    }

    const newPackageItems = [...formData.packageItems, {
      productId: selectedPackageProduct,
      quantity: packageQuantity
    }];

    setFormData({
      ...formData,
      packageItems: newPackageItems
    });

    calculateOriginalPrice(newPackageItems);
    setSelectedPackageProduct('');
    setPackageQuantity(1);
  };

  const handleRemovePackageItem = (index) => {
    const newPackageItems = formData.packageItems.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      packageItems: newPackageItems
    });
    calculateOriginalPrice(newPackageItems);
  };

  const calculateOriginalPrice = (packageItems) => {
    let total = 0;
    packageItems.forEach(item => {
      const product = products.find(p => p._id === item.productId);
      if (product) {
        total += product.price * (item.quantity || 1);
      }
    });
    setOriginalPrice(total);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        image: file,
        imagePreview: imageUrl
      });
    }
  };

  const handleFileButtonClick = () => {
    const input = document.querySelector('input[name="image"]');
    if (input) {
      input.click();
    }
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      image: null,
      imagePreview: null
    });
    // Input'u temizle
    const input = document.querySelector('input[name="image"]');
    if (input) {
      input.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Yeni ürün eklerken görsel zorunlu
    if (!editingProduct && !formData.image) {
      toast({
        title: 'Hata',
        description: 'Lütfen bir ürün görseli seçin.',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    
    // Alerjen kontrolü
    if (!formData.allergens || formData.allergens.length === 0) {
      toast({
        title: 'Hata',
        description: 'Lütfen en az bir alerjen seçin veya "Alerjen Yok" seçeneğini işaretleyin.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Hazırlanma süresi kontrolü
    if (!formData.preparationTimeMin || !formData.preparationTimeMax || formData.preparationTimeMin <= 0 || formData.preparationTimeMax <= 0) {
      toast({
        title: 'Hata',
        description: 'Hazırlanma süresi zorunludur ve 0\'dan büyük olmalıdır',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // İçindekiler kontrolü
    if (!formData.ingredients || formData.ingredients.length === 0) {
      toast({
        title: 'Hata',
        description: 'En az bir içindekiler eklemelisiniz',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const caloriesValue = Number(formData?.nutritionInfo?.calories);
    if (!Number.isFinite(caloriesValue) || caloriesValue <= 0) {
      toast({
        title: 'Hata',
        description: 'Kalori (kcal) zorunludur. Lütfen 0’dan büyük bir değer girin.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('price', formData.price);
    submitData.append('vatRate', formData.vatRate || 10);
    submitData.append('categoryId', formData.categoryId);
    submitData.append('description', formData.description || '');
    submitData.append('ingredients', JSON.stringify(formData.ingredients || []));
    submitData.append('extras', JSON.stringify(formData.extras || []));
    submitData.append('preparationTimeMin', formData.preparationTimeMin);
    submitData.append('preparationTimeMax', formData.preparationTimeMax);
    submitData.append('isActive', formData.isActive);
    submitData.append('isFeatured', formData.isFeatured);
    submitData.append('isPackage', formData.isPackage);
    
    if (formData.isPackage) {
      submitData.append('packageItems', JSON.stringify(formData.packageItems));
    }
    
    if (formData.image) {
      submitData.append('image', formData.image);
    }

    // Tags ekle (boş array olsa bile gönder)
    submitData.append('tags', JSON.stringify(formData.tags || []));

    // Allergens ekle (boş array olsa bile gönder - zorunlu alan)
    submitData.append('allergens', JSON.stringify(formData.allergens || []));

    submitData.append(
      'nutritionInfo',
      JSON.stringify({
        calories: caloriesValue,
      })
    );

    // Options ekle
    submitData.append('options', JSON.stringify(formData.options || []));

    onAdd(submitData);
    // onClose() çağrısını kaldırdık çünkü ProductTable'da handleCloseModal zaten çağrılıyor
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim().toLowerCase())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim().toLowerCase()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  const handleAddAllergen = () => {
    if (newAllergen.trim() && !formData.allergens.includes(newAllergen.trim())) {
      setFormData({
        ...formData,
        allergens: [...formData.allergens, newAllergen.trim()]
      });
      setNewAllergen('');
    }
  };

  const handleRemoveAllergen = (allergen) => {
    setFormData({
      ...formData,
      allergens: formData.allergens.filter(a => a !== allergen)
    });
  };

  const handleNutritionChange = (field, value) => {
    setFormData({
      ...formData,
      nutritionInfo: {
        ...formData.nutritionInfo,
        [field]: value
      }
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent maxW="90vw">
        <ModalHeader color="#e45858">
          {editingProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Ürün Aktifliği ve Öne Çıkan - Yan Yana */}
          <SimpleGrid columns={3} spacing={4} mb={6}>
            <FormControl>
              <VStack align="start" spacing={2}>
                <FormLabel mb="0" fontWeight="bold">Ürün Aktif Mi?</FormLabel>
                <Switch
                  name="isActive"
                  isChecked={formData.isActive}
                  onChange={(e) => handleChange({
                    target: { name: 'isActive', value: e.target.checked }
                  })}
                  colorScheme="red"
                  size="lg"
                />
              </VStack>
            </FormControl>
            
            <FormControl>
              <VStack align="start" spacing={2}>
                <FormLabel mb="0" fontWeight="bold">Öne Çıkan Ürün</FormLabel>
                <Text fontSize="xs" color="gray.600">
                  Altın badge ile gösterilir
                </Text>
                <Switch
                  name="isFeatured"
                  isChecked={formData.isFeatured}
                  onChange={(e) => handleChange({
                    target: { name: 'isFeatured', value: e.target.checked }
                  })}
                  colorScheme="yellow"
                  size="lg"
                />
              </VStack>
            </FormControl>

            <FormControl>
              <VStack align="start" spacing={2}>
                <FormLabel mb="0" fontWeight="bold">Menü Paketi</FormLabel>
                <Text fontSize="xs" color="gray.600">
                  Birden fazla ürünü birleştir
                </Text>
                <Switch
                  name="isPackage"
                  isChecked={formData.isPackage}
                  onChange={handlePackageToggle}
                  colorScheme="blue"
                  size="lg"
                />
              </VStack>
            </FormControl>
          </SimpleGrid>

          <Divider mb={6} />

          {/* Ürün Adı ve Fiyat - Yan yana */}
          <HStack spacing={4} mb={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="bold">Ürün Adı</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ürün adını girin"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontWeight="bold">
                {formData.isPackage ? 'Paket Fiyatı (İndirimli - KDV Dahil)' : 'Fiyat (KDV Dahil)'}
              </FormLabel>
              <Input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder={formData.isPackage ? "İndirimli paket fiyatı" : "KDV dahil fiyatı girin"}
              />
              {formData.isPackage && originalPrice > 0 && (
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Normal fiyat: {originalPrice.toFixed(2)} ₺ | 
                  İndirim: {((originalPrice - Number(formData.price || 0)) / originalPrice * 100).toFixed(0)}%
                </Text>
              )}
              {formData.price && formData.vatRate && (
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Girdiğiniz fiyat KDV dahildir. Seçtiğiniz %{formData.vatRate} KDV oranına göre işletmeye {((Number(formData.price) / (1 + formData.vatRate / 100))).toFixed(2)} ₺ kalacaktır.
                </Text>
              )}
            </FormControl>
          </HStack>

          {/* KDV Oranı Seçimi */}
          <FormControl isRequired mb={6}>
            <FormLabel fontWeight="bold">
              KDV Oranı (%)
              <Tooltip
                label={
                  <Box>
                    <Text fontWeight="bold" mb={2}>Türkiye'de Gıda Sektörü KDV Oranları:</Text>
                    <Text mb={1}>• %1: Temel gıda maddeleri (ekmek, un, makarna, pirinç, bakliyat, süt, yumurta, peynir, yoğurt, et, tavuk, balık, sebze, meyve, çay, şeker, tuz, baharat)</Text>
                    <Text mb={1}>• %10: Hazır gıdalar, işlenmiş gıdalar, içecekler (alkolsüz), dondurulmuş gıdalar</Text>
                    <Text>• %20: Alkol, sigara, lüks gıdalar</Text>
                  </Box>
                }
                hasArrow
                placement="top"
                bg="gray.700"
                color="white"
                fontSize="sm"
                maxW="400px"
              >
                <IconButton
                  icon={<InfoIcon />}
                  size="xs"
                  variant="ghost"
                  colorScheme="blue"
                  ml={2}
                  aria-label="KDV bilgisi"
                />
              </Tooltip>
            </FormLabel>
            <Select
              name="vatRate"
              value={formData.vatRate}
              onChange={handleChange}
            >
              <option value={1}>%1 - Temel Gıda Maddeleri</option>
              <option value={10}>%10 - Hazır/İşlenmiş Gıdalar (Varsayılan)</option>
              <option value={20}>%20 - Alkol, Sigara, Lüks Gıdalar</option>
            </Select>
            <Text fontSize="xs" color="gray.600" mt={1}>
              Seçilmezse otomatik olarak %10 uygulanacaktır
            </Text>
            <Text fontSize="xs" color="orange.600" mt={1} fontWeight="semibold">
              ⚠️ Girdiğiniz tüm fiyatlarda (ürün, ekstra, seçenek) seçmiş olduğunuz KDV oranı dahil edilecektir.
            </Text>
          </FormControl>

          {/* Kategori ve Görsel - Yan yana */}
          <HStack spacing={4} mb={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="bold">Kategori</FormLabel>
              <Select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                placeholder="Kategori Seçin"
              >
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired={!editingProduct}>
              <FormLabel fontWeight="bold">Ürün Görseli {!editingProduct && '(Zorunlu)'}</FormLabel>
              <Input
                type="file"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                display="none"
                id="product-image-input"
              />
              {formData.image ? (
                <Box
                  p={4}
                  borderRadius="12px"
                  bg="gray.50"
                  border="2px dashed"
                  borderColor="#e45858"
                  position="relative"
                >
                  <Flex align="center" justify="space-between">
                    <Flex align="center" gap={3}>
                      <Icon as={MdAttachFile} color="#e45858" w={6} h={6} />
                      <Box>
                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                          {formData.image.name}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {(formData.image.size / 1024 / 1024).toFixed(2)} MB
                        </Text>
                      </Box>
                    </Flex>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={removeImage}
                      _hover={{ bg: "red.50" }}
                    >
                      ✕
                    </Button>
                  </Flex>
                  {(formData.imagePreview || existingImageUrl) && (
                    <Image
                      src={formData.imagePreview || existingImageUrl}
                      alt="Ürün önizleme"
                      maxH="100px"
                      mt={3}
                      borderRadius="8px"
                    />
                  )}
                </Box>
              ) : existingImageUrl ? (
                <Box
                  p={4}
                  borderRadius="12px"
                  bg="gray.50"
                  border="2px solid"
                  borderColor="gray.200"
                >
                  <Flex align="center" justify="space-between" mb={3}>
                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                      Mevcut Görsel
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        setExistingImageUrl(null);
                        setFormData({ ...formData, image: null });
                      }}
                      _hover={{ bg: "red.50" }}
                    >
                      ✕ Kaldır
                    </Button>
                  </Flex>
                  <Image
                    src={existingImageUrl}
                    alt="Mevcut ürün görseli"
                    maxH="100px"
                    borderRadius="8px"
                  />
                  <Button
                    size="sm"
                    mt={3}
                    leftIcon={<Icon as={MdCloudUpload} />}
                    onClick={handleFileButtonClick}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Yeni Görsel Seç
                  </Button>
                </Box>
              ) : (
                <Box
                  as="button"
                  type="button"
                  onClick={handleFileButtonClick}
                  w="100%"
                  p={6}
                  borderRadius="12px"
                  bg="gray.50"
                  border="2px dashed"
                  borderColor="gray.300"
                  _hover={{ 
                    borderColor: "#e45858",
                    bg: "gray.100"
                  }}
                  transition="all 0.2s"
                  cursor="pointer"
                >
                  <Flex direction="column" align="center" gap={2}>
                    <Icon as={MdCloudUpload} color="#e45858" w={8} h={8} />
                    <Text fontSize="sm" fontWeight="500" color="gray.800">
                      Görsel Seç
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      PNG, JPG (Max 10MB)
                      <br />
                      Önerilen: 1000x1000px (1:1 oran)
                    </Text>
                  </Flex>
                </Box>
              )}
            </FormControl>
          </HStack>

          {/* Ürün Açıklaması ve Hazırlanma Süresi - Yan Yana */}
          <SimpleGrid columns={2} spacing={4} mb={6}>
            <FormControl>
              <FormLabel fontWeight="bold">Ürün Açıklaması</FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Ürün hakkında bilgi girin (isteğe bağlı)"
                rows={3}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Her satır bir özellik olarak gösterilir.
              </Text>
            </FormControl>

            {/* Hazırlanma Süresi */}
            <FormControl isRequired>
              <FormLabel fontWeight="bold">Hazırlanma Süresi (Dakika)</FormLabel>
              <HStack>
                <NumberInput
                  value={formData.preparationTimeMin}
                  onChange={(value) => handlePrepTimeChange('preparationTimeMin', value)}
                  min={0}
                >
                  <NumberInputField placeholder="Min" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text>-</Text>
                <NumberInput
                  value={formData.preparationTimeMax}
                  onChange={(value) => handlePrepTimeChange('preparationTimeMax', value)}
                  min={formData.preparationTimeMin}
                >
                  <NumberInputField placeholder="Max" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </HStack>
            </FormControl>
          </SimpleGrid>

          <Divider mb={6} />

          {/* Paket Ürünleri */}
          {formData.isPackage && (
            <>
              <FormControl mb={6}>
                <FormLabel fontWeight="bold">Paket İçeriği</FormLabel>
                <VStack align="stretch" spacing={3}>
                  <HStack>
                    <Select
                      value={selectedPackageProduct}
                      onChange={(e) => setSelectedPackageProduct(e.target.value)}
                      placeholder="Ürün seçin"
                      flex={2}
                    >
                      {products
                        .filter(p => !p.isPackage && p._id) // Paket olmayan ve mevcut ürünler
                        .map((product) => (
                          <option key={product._id} value={product._id}>
                            {product.name} - {product.price} ₺
                          </option>
                        ))}
                    </Select>
                    <NumberInput
                      value={packageQuantity}
                      onChange={(value) => setPackageQuantity(Number(value))}
                      min={1}
                      flex={1}
                    >
                      <NumberInputField placeholder="Adet" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <IconButton
                      icon={<AddIcon />}
                      onClick={handleAddPackageItem}
                      colorScheme="blue"
                      variant="outline"
                    />
                  </HStack>
                  
                  {formData.packageItems.length > 0 && (
                    <VStack align="stretch" spacing={2} p={3} bg="blue.50" borderRadius="md">
                      {formData.packageItems.map((item, index) => {
                        const product = products.find(p => p._id === item.productId);
                        return (
                          <HStack key={index} justify="space-between">
                            <Text>
                              {product?.name || 'Ürün bulunamadı'} x {item.quantity} = 
                              {(product?.price || 0) * item.quantity} ₺
                            </Text>
                            <IconButton
                              size="sm"
                              icon={<MinusIcon />}
                              onClick={() => handleRemovePackageItem(index)}
                              colorScheme="red"
                              variant="ghost"
                            />
                          </HStack>
                        );
                      })}
                      <Divider />
                      <HStack justify="space-between" fontWeight="bold">
                        <Text>Toplam Normal Fiyat:</Text>
                        <Text color="gray.600">{originalPrice.toFixed(2)} ₺</Text>
                      </HStack>
                      {formData.price && (
                        <HStack justify="space-between" fontWeight="bold" color="green.600">
                          <Text>Paket Fiyatı:</Text>
                          <Text>{Number(formData.price).toFixed(2)} ₺</Text>
                        </HStack>
                      )}
                    </VStack>
                  )}
                </VStack>
              </FormControl>
              <Divider mb={6} />
            </>
          )}

          {/* İçindekiler ve Ekstralar - Yan Yana */}
          <SimpleGrid columns={2} spacing={4} mb={6}>
            {/* İçindekiler */}
            <FormControl isRequired>
              <FormLabel fontWeight="bold">İçindekiler</FormLabel>
              <VStack align="stretch" spacing={2}>
                <HStack>
                  <Input
                    value={newIngredient.name}
                    onChange={(e) => setNewIngredient({...newIngredient, name: e.target.value})}
                    placeholder="Malzeme adı"
                    size="sm"
                  />
                  <Checkbox
                    isChecked={newIngredient.removable}
                    onChange={(e) => setNewIngredient({...newIngredient, removable: e.target.checked})}
                    size="sm"
                  >
                    Çıkarılabilir
                  </Checkbox>
                  <IconButton
                    icon={<AddIcon />}
                    onClick={handleAddIngredient}
                    colorScheme="red"
                    variant="outline"
                    size="sm"
                  />
                </HStack>
                {formData.ingredients.map((item, index) => (
                  <HStack key={index} justify="space-between">
                    <Text fontSize="sm">• {item.name}</Text>
                    <Badge colorScheme={item.removable ? "green" : "red"} fontSize="xs">
                      {item.removable ? "Çıkarılabilir" : "Sabit"}
                    </Badge>
                    <IconButton
                      size="xs"
                      icon={<MinusIcon />}
                      onClick={() => handleRemoveIngredient(index)}
                      colorScheme="red"
                      variant="ghost"
                    />
                  </HStack>
                ))}
              </VStack>
            </FormControl>

            {/* Ekstralar - Paket değilse göster */}
            {!formData.isPackage && (
              <FormControl>
                <FormLabel fontWeight="bold">Ekstralar (KDV Dahil)</FormLabel>
                <VStack align="stretch" spacing={2}>
                  <HStack>
                    <Input
                      value={newExtra.name}
                      onChange={(e) => setNewExtra({...newExtra, name: e.target.value})}
                      placeholder="Ekstra adı"
                      size="sm"
                    />
                    <NumberInput
                      value={newExtra.price}
                      onChange={(value) => setNewExtra({...newExtra, price: value})}
                      min={0}
                      size="sm"
                    >
                      <NumberInputField placeholder="KDV dahil fiyat" />
                    </NumberInput>
                    <IconButton
                      icon={<AddIcon />}
                      onClick={handleAddExtra}
                      colorScheme="red"
                      variant="outline"
                      size="sm"
                    />
                  </HStack>
                  {newExtra.price && formData.vatRate && (
                    <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                      Girdiğiniz fiyat KDV dahildir. İşletmeye {((Number(newExtra.price) / (1 + formData.vatRate / 100))).toFixed(2)} ₺ kalacaktır.
                    </Text>
                  )}
                  {formData.extras.map((extra, index) => {
                    // Fiyat zaten KDV dahil
                    const extraPrice = Number(extra.price) || 0;
                    return (
                    <HStack key={index} justify="space-between">
                        <VStack align="start" spacing={0}>
                      <Text fontSize="sm">• {extra.name}</Text>
                          <Text fontSize="xs" color="gray.600">
                            KDV Dahil: {extraPrice.toFixed(2)} ₺
                          </Text>
                        </VStack>
                      <IconButton
                        size="xs"
                        icon={<MinusIcon />}
                        onClick={() => handleRemoveExtra(index)}
                        colorScheme="red"
                        variant="ghost"
                      />
                    </HStack>
                    );
                  })}
                </VStack>
              </FormControl>
            )}
          </SimpleGrid>

          <Divider mb={6} />

          {/* Tags ve Kalori - Yan Yana */}
          <SimpleGrid columns={2} spacing={4} mb={6}>
            {/* Tags */}
            <FormControl>
              <FormLabel fontWeight="bold">Etiketler (Tags)</FormLabel>
              <Text fontSize="xs" color="gray.600" mb={2}>
                Örn: vegan, vejetaryen, glutensiz
              </Text>
              <VStack align="stretch" spacing={2}>
                <HStack>
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Etiket ekle"
                    size="sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <IconButton
                    icon={<AddIcon />}
                    onClick={handleAddTag}
                    colorScheme="red"
                    variant="outline"
                    size="sm"
                  />
                </HStack>
                {/* Manuel tag butonları */}
                <HStack flexWrap="wrap" spacing={2}>
                  {availableTags.map((tag) => (
                    <Badge
                      key={tag}
                      colorScheme={formData.tags.includes(tag) ? 'red' : 'gray'}
                      cursor="pointer"
                      onClick={() => {
                        if (formData.tags.includes(tag)) {
                          handleRemoveTag(tag);
                        } else {
                          setFormData({
                            ...formData,
                            tags: [...formData.tags, tag]
                          });
                        }
                      }}
                      p={1.5}
                      fontSize="xs"
                    >
                      {tag}
                    </Badge>
                  ))}
                </HStack>
                {/* Eklenen tagler */}
                {formData.tags.length > 0 && (
                  <HStack flexWrap="wrap" spacing={2} mt={1}>
                    {formData.tags.map((tag) => (
                      <Badge
                        key={tag}
                        colorScheme="red"
                        p={1.5}
                        fontSize="xs"
                      >
                        {tag}
                        <IconButton
                          size="xs"
                          icon={<MinusIcon />}
                          onClick={() => handleRemoveTag(tag)}
                          colorScheme="red"
                          variant="ghost"
                          ml={1}
                        />
                      </Badge>
                    ))}
                  </HStack>
                )}
              </VStack>
            </FormControl>

            {/* Nutrition Info - Sadece Kalori */}
            <FormControl isRequired>
              <FormLabel fontWeight="bold">Kalori (kcal)</FormLabel>
              <Text fontSize="xs" color="gray.600" mb={3}>
                Zorunlu. Mobilde yaklaşık değer olarak gösterilir.
              </Text>
              <NumberInput
                value={formData.nutritionInfo.calories}
                onChange={(value) => handleNutritionChange('calories', value)}
                min={1}
                max={10000}
              >
                <NumberInputField placeholder="Kalori (kcal)" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </SimpleGrid>

          {/* Allergens */}
          <FormControl mb={6} isRequired>
            <FormLabel fontWeight="bold">Alerjenler</FormLabel>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Ürünün içerdiği alerjenleri işaretleyin. Eğer hiç alerjen yoksa "Alerjen Yok" seçeneğini işaretleyin.
            </Text>
            <VStack align="stretch" spacing={3}>
              {/* Standart Alerjen Listesi */}
              <Box
                p={4}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text fontSize="sm" fontWeight="bold" mb={3}>Standart Alerjenler</Text>
                <SimpleGrid columns={2} spacing={2}>
                  {/* Alerjen Yok seçeneği */}
                  <Checkbox
                    isChecked={formData.allergens.length === 0 || formData.allergens.includes('alerjen_yok')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          allergens: ['alerjen_yok']
                        });
                      } else {
                        setFormData({
                          ...formData,
                          allergens: []
                        });
                      }
                    }}
                    colorScheme="green"
                  >
                    <Text fontSize="sm" fontWeight="bold" color="green.700">Alerjen Yok</Text>
                  </Checkbox>
                  {standardAllergens.map((allergen) => {
                    const isSelected = formData.allergens.includes(allergen.value);
                    return (
                      <Checkbox
                        key={allergen.value}
                        isChecked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Alerjen yok seçiliyse kaldır
                            const newAllergens = formData.allergens.filter(a => a !== 'alerjen_yok');
                            if (!newAllergens.includes(allergen.value)) {
                              setFormData({
                                ...formData,
                                allergens: [...newAllergens, allergen.value]
                              });
                            }
                          } else {
                            handleRemoveAllergen(allergen.value);
                          }
                        }}
                        colorScheme="red"
                        isDisabled={formData.allergens.includes('alerjen_yok') && !isSelected}
                      >
                        <Text fontSize="sm">{allergen.label}</Text>
                      </Checkbox>
                    );
                  })}
                </SimpleGrid>
              </Box>

              {/* Manuel Alerjen Ekleme */}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>Özel Alerjen Ekle</Text>
                <HStack>
                  <Input
                    value={newAllergen}
                    onChange={(e) => setNewAllergen(e.target.value)}
                    placeholder="Standart listede yoksa buraya yazın"
                    size="sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddAllergen();
                      }
                    }}
                  />
                  <IconButton
                    icon={<AddIcon />}
                    onClick={handleAddAllergen}
                    colorScheme="red"
                    variant="outline"
                    size="sm"
                  />
                </HStack>
              </Box>

              {/* Seçilen Alerjenler Özeti */}
              {formData.allergens.length > 0 && (
                <Box
                  p={3}
                  bg="red.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="red.200"
                >
                  <Text fontSize="sm" fontWeight="bold" mb={2} color="red.700">
                    Seçilen Alerjenler ({formData.allergens.length})
                  </Text>
                  <HStack flexWrap="wrap" spacing={2}>
                    {formData.allergens.map((allergen) => {
                      const allergenInfo = standardAllergens.find(a => a.value === allergen);
                      return (
                        <Badge
                          key={allergen}
                          colorScheme="red"
                          p={2}
                          fontSize="xs"
                        >
                          {allergenInfo ? allergenInfo.label : allergen}
                          <IconButton
                            size="xs"
                            icon={<MinusIcon />}
                            onClick={() => handleRemoveAllergen(allergen)}
                            colorScheme="red"
                            variant="ghost"
                            ml={2}
                            aria-label="Kaldır"
                          />
                        </Badge>
                      );
                    })}
                  </HStack>
                </Box>
              )}
            </VStack>
          </FormControl>

          {/* Options (Seçenekler) - Boyut, Kenar Tipi vs */}
          <FormControl mb={6}>
            <FormLabel fontWeight="bold">Ürün Seçenekleri</FormLabel>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Boyut, kenar tipi gibi seçenekler ekleyin. Bir seçenek standart (ücretsiz) olmalı, diğerleri ücretli olabilir.
            </Text>
            
            <VStack align="stretch" spacing={4}>
              {/* Yeni Option Ekleme Formu */}
              <Box
                p={4}
                bg="blue.50"
                borderRadius="md"
                border="1px solid"
                borderColor="blue.200"
              >
                <Text fontSize="sm" fontWeight="bold" mb={3}>Yeni Seçenek Ekle</Text>
                
                <VStack align="stretch" spacing={3}>
                  {/* Option Adı */}
                  <HStack>
                    <Input
                      value={newOption.name}
                      onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                      placeholder="Örn: Boyut, Kenar Tipi"
                      size="sm"
                      flex={1}
                    />
                    <Checkbox
                      isChecked={newOption.required}
                      onChange={(e) => setNewOption({ ...newOption, required: e.target.checked })}
                      colorScheme="red"
                    >
                      <Text fontSize="xs">Zorunlu</Text>
                    </Checkbox>
                  </HStack>

                  {/* Choice Ekleme */}
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" mb={2}>Seçimler (En az bir tanesi 0 TL olmalı - standart)</Text>
                    <HStack>
                      <Input
                        value={newChoice.name}
                        onChange={(e) => setNewChoice({ ...newChoice, name: e.target.value })}
                        placeholder="Örn: Küçük, Orta, Büyük"
                        size="sm"
                        flex={1}
                      />
                      <NumberInput
                        value={newChoice.price}
                        onChange={(value) => setNewChoice({ ...newChoice, price: Number(value) || 0 })}
                        min={0}
                        size="sm"
                        w="120px"
                      >
                        <NumberInputField placeholder="KDV dahil fiyat" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      {newChoice.price > 0 && formData.vatRate && (
                        <Text fontSize="xs" color="gray.600" fontWeight="semibold" ml={2}>
                          Girdiğiniz fiyat KDV dahildir. İşletmeye {((Number(newChoice.price) / (1 + formData.vatRate / 100))).toFixed(2)} ₺ kalacaktır.
                        </Text>
                      )}
                      <IconButton
                        icon={<AddIcon />}
                        onClick={handleAddChoiceToOption}
                        colorScheme="blue"
                        variant="outline"
                        size="sm"
                      />
                    </HStack>
                  </Box>

                  {/* Eklenen Choices Listesi */}
                  {newOption.choices.length > 0 && (
                    <Box
                      p={2}
                      bg="white"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Text fontSize="xs" fontWeight="bold" mb={2}>Seçimler:</Text>
                      <VStack align="stretch" spacing={1}>
                        {newOption.choices.map((choice, idx) => {
                          // choice.price zaten KDV dahil
                          const choicePrice = Number(choice.price) || 0;
                          return (
                            <VStack key={idx} align="start" spacing={0} p={1} bg={choicePrice === 0 ? 'green.50' : 'gray.50'} borderRadius="sm">
                              <HStack justify="space-between" w="100%">
                            <Text fontSize="xs">
                                  {choice.name} {choicePrice === 0 ? '(Standart - Ücretsiz)' : `(+${choicePrice.toFixed(2)}₺)`}
                            </Text>
                            <IconButton
                              size="xs"
                              icon={<MinusIcon />}
                              onClick={() => handleRemoveChoiceFromOption(idx)}
                              colorScheme="red"
                              variant="ghost"
                            />
                          </HStack>
                              {choicePrice > 0 && (
                                <Text fontSize="xs" color="gray.600" ml={2}>
                                  KDV Dahil: {choicePrice.toFixed(2)} ₺
                                </Text>
                              )}
                            </VStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {/* Option'ı Ekle Butonu */}
                  <Button
                    onClick={handleAddOption}
                    colorScheme="blue"
                    size="sm"
                    leftIcon={<AddIcon />}
                    isDisabled={!newOption.name.trim() || newOption.choices.length === 0}
                  >
                    Seçeneği Ekle
                  </Button>
                </VStack>
              </Box>

              {/* Eklenen Options Listesi */}
              {formData.options.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>Eklenen Seçenekler ({formData.options.length})</Text>
                  <VStack align="stretch" spacing={2}>
                    {formData.options.map((option, optionIdx) => (
                      <Box
                        key={optionIdx}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <HStack justify="space-between" mb={2}>
                          <HStack>
                            <Text fontSize="sm" fontWeight="bold">{option.name}</Text>
                            {option.required && (
                              <Badge colorScheme="red" fontSize="xs">Zorunlu</Badge>
                            )}
                          </HStack>
                          <IconButton
                            size="xs"
                            icon={<MinusIcon />}
                            onClick={() => handleRemoveOption(optionIdx)}
                            colorScheme="red"
                            variant="ghost"
                          />
                        </HStack>
                        <VStack align="stretch" spacing={1}>
                          {option.choices.map((choice, choiceIdx) => {
                            // choice.price zaten KDV dahil (backend'den geliyor veya handleAddChoiceToOption'da hesaplandı)
                            const priceWithVat = Number(choice.price) || 0;
                            return (
                              <VStack key={choiceIdx} align="start" spacing={0} fontSize="xs">
                                <HStack w="100%" justify="space-between">
                              <Text flex={1}>{choice.name}</Text>
                                  <VStack align="end" spacing={0}>
                                    <Text fontWeight="bold" color={priceWithVat === 0 ? 'green.600' : 'gray.700'}>
                                      {priceWithVat === 0 ? 'Standart (0₺)' : `+${priceWithVat.toFixed(2)}₺`}
                              </Text>
                                    {priceWithVat > 0 && (
                                      <Text fontSize="xs" color="gray.600">
                                        KDV Dahil
                                      </Text>
                                    )}
                                  </VStack>
                            </HStack>
                              </VStack>
                            );
                          })}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </FormControl>

        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            İptal
          </Button>
          <Button colorScheme="red" onClick={handleSubmit}>
            {editingProduct ? 'Güncelle' : 'Kaydet'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddProductModal;
